import { AssetClassesQuery } from '@/__generated__/graphql';
import { InfoIcon } from '@/assets/icons/info-icons/info';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import Button from '@/components/basicComponents/button';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Input from '@/components/basicComponents/input';
import { formatDollarInputValue, formatMaskedDollarInputValue } from '@/components/basicComponents/input/utils';
import Label from '@/components/basicComponents/label';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { TwoColumnWrapper } from '@/components/transactions-page/generateSignaturePacket-page/templateComponents/styles';
import { useErrorMessages } from '@/hooks/i18n';
import { useResponsive } from '@/hooks/use-responsive';
import { useGoBack } from '@/hooks/useGoBack';
import { useStatusData } from '@/hooks/useStatusData';
import { MainWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { useMutation } from '@apollo/client';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { InvestorDetailsEntityTypeEnum } from '../../investorDetailsPage/types';
import { EditAssetClassTargets, RadioButtonWithNotification } from '../components';
import {
  editInvestorAllocationTargetsAssetClassTargetsBannerDescription,
  editInvestorAllocationTargetsAssetClassTargetsOptions,
  editInvestorAllocationTargetsCapacityCalculationBannerDescriptions,
  editInvestorAllocationTargetsCapacityCalculationOptions,
  editInvestorAllocationTargetsTotalEntityValueTooltips
} from '../constants';
import { UPDATE_LEGAL_ENTITY_MUTATION } from '../queries';
import {
  EditInvestorFormDevider,
  EditInvestorFormFieldsWrapper,
  EditInvestorFormFooter,
  EditInvestorModalPageForm,
  EditInvestorModalPageWrapper
} from '../styles';
import {
  EditInvestorAllocationTargetsAssetClassTargetsOptionEnum,
  EditInvestorAllocationTargetsCapacityCalculationOptionEnum,
  IEditInvestorFormData
} from '../types';
import { buildAllocationTargetsToUpdate } from '../utils';

interface EditAllocationTargetsModalPageProps {
  entityType: InvestorDetailsEntityTypeEnum;
  selectedGroupName: string;
  assetClasses: AssetClassesQuery['assetClasses'];
  allocationTargetsFormData: IEditInvestorFormData['allocationTargets'];
  refetchEntityOrGroupData: () => Promise<void>;
  handleCloseModalPage: () => void;
}

export const EditAllocationTargetsModalPage = ({
  entityType,
  selectedGroupName,
  assetClasses,
  allocationTargetsFormData,
  refetchEntityOrGroupData,
  handleCloseModalPage
}: EditAllocationTargetsModalPageProps) => {
  const goBack = useGoBack();
  const params = useParams();
  const location = useLocation();
  const { isMobile } = useResponsive();
  const { data: statusData } = useStatusData();
  const { default: defaultErrorMessage } = useErrorMessages();
  const {
    control,
    formState: { isSubmitting },
    reset,
    watch,
    handleSubmit
  } = useForm<IEditInvestorFormData['allocationTargets']>();

  const entityId = params?.entityId || '';
  const returnToInvestorDetails = location?.state?.returnToInvestorDetails || false;

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [updateLegalEntity] = useMutation(UPDATE_LEGAL_ENTITY_MUTATION);

  useEffect(() => {
    if (!allocationTargetsFormData) return;
    reset(allocationTargetsFormData);
  }, [allocationTargetsFormData]);

  const handleClose = () => {
    if (returnToInvestorDetails) {
      goBack({ fallBack: '/investors' });
      return;
    }
    handleCloseModalPage();
  };

  const onSubmit = async (data: IEditInvestorFormData['allocationTargets']) => {
    try {
      setErrorMessage(null);

      const { totalEntityValue, overCommitRatio, assetClassTargetsOption, capacityCalculationOption, allocationTargets } = data;
      const ignoreAllocationTargets =
        capacityCalculationOption === EditInvestorAllocationTargetsCapacityCalculationOptionEnum.DisregardAllocationTargets;
      const isOverCommitmentRatioCapacityCalculation =
        capacityCalculationOption === EditInvestorAllocationTargetsCapacityCalculationOptionEnum.OverCommitmentRatio;
      const useDefaultAssetClassTargets =
        assetClassTargetsOption === EditInvestorAllocationTargetsAssetClassTargetsOptionEnum.UseDefaultAssetClassTargets;

      const privatePercentage = Object.values(allocationTargets).reduce((acc, target) => acc + parseFloat(target || '0'), 0);
      const allocationTargetsToUpdate = buildAllocationTargetsToUpdate(allocationTargets, assetClasses, statusData?.target_sub_asset_classes);

      if (entityType === InvestorDetailsEntityTypeEnum.GroupedEntity) {
        await updateLegalEntity({
          variables: {
            data: {
              id: entityId,
              totalEntityValue: parseFloat(totalEntityValue)
            }
          }
        });
      } else {
        await updateLegalEntity({
          variables: {
            data: {
              id: entityId,
              ignoreAllocationTargets,
              ...(!ignoreAllocationTargets && {
                allocationTargets: useDefaultAssetClassTargets ? [] : allocationTargetsToUpdate,
                privatePercentage: privatePercentage,
                ...(entityType !== InvestorDetailsEntityTypeEnum.Group && {
                  totalEntityValue: parseFloat(totalEntityValue)
                }),
                overCommitRatio: isOverCommitmentRatioCapacityCalculation ? parseFloat('1.' + overCommitRatio) : null
              })
            }
          }
        });
      }

      await refetchEntityOrGroupData();
      handleClose();
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
    }
  };

  return (
    <>
      {isSubmitting && (
        <ModalWrappedContent isOpen={isSubmitting} title="Saving" showCancelButton={false} showCloseIcon={false} onClose={() => {}}>
          <ModalLoader />
        </ModalWrappedContent>
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={handleClose} backToTitle="Edit Investor" />} />
        <PageTitle title="Allocation Targets" />
        {errorMessage && (
          <Banner
            icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
            title="Error"
            description={errorMessage}
            bgColor={themeVar('context.error')}
          />
        )}
        <EditInvestorModalPageWrapper>
          <EditInvestorModalPageForm onSubmit={handleSubmit(onSubmit)}>
            <EditInvestorFormFieldsWrapper>
              {entityType === InvestorDetailsEntityTypeEnum.GroupedEntity ? (
                <>
                  <TwoColumnWrapper isMobile={isMobile}>
                    <Controller
                      name="totalEntityValue"
                      control={control}
                      rules={{
                        required: {
                          value: true,
                          message: 'Total Entity Value is required'
                        }
                      }}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <Input
                          value={value ? formatMaskedDollarInputValue(value) : ''}
                          onChange={(e) => {
                            const formattedValue = formatDollarInputValue(e.target.value);
                            onChange(formattedValue);
                          }}
                          errorText={error?.message}
                          label="Total Entity Value"
                          tooltipWidth={400}
                          tooltip={editInvestorAllocationTargetsTotalEntityValueTooltips[entityType]}
                          isRequiredField
                          withDollarSign
                          size="md"
                          styleType="form"
                        />
                      )}
                    />
                  </TwoColumnWrapper>
                  <Banner
                    icon={<InfoIcon fill={themeVar('menu.tab')} />}
                    title=""
                    description={`Refer to ${selectedGroupName} for allocation targets and related settings. Grouped entities do not have allocation targets of their own.`}
                    color={themeVar('font.base')}
                    bgColor={themeVar('layer.base')}
                    dividerColor={themeVar('border.base')}
                    withBorderRadius
                  />
                </>
              ) : (
                <>
                  <RadioButtonWithNotification
                    control={control}
                    name="capacityCalculationOption"
                    label="Capacity Calculation"
                    options={editInvestorAllocationTargetsCapacityCalculationOptions}
                    bannerDescription={editInvestorAllocationTargetsCapacityCalculationBannerDescriptions[watch('capacityCalculationOption')]}
                  />
                  <AnimatePresence>
                    {watch('capacityCalculationOption') !== EditInvestorAllocationTargetsCapacityCalculationOptionEnum.DisregardAllocationTargets && (
                      <motion.div key="capacityCalculation" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <EditInvestorFormFieldsWrapper>
                          <TwoColumnWrapper isMobile={isMobile}>
                            <Controller
                              name="totalEntityValue"
                              control={control}
                              rules={{
                                ...(entityType !== InvestorDetailsEntityTypeEnum.Group && {
                                  required: {
                                    value: true,
                                    message: 'Total Entity Value is required'
                                  },
                                  min: {
                                    value: 1,
                                    message: 'Total Entity Value must be greater than 0'
                                  }
                                })
                              }}
                              render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <Input
                                  value={value ? formatMaskedDollarInputValue(value) : ''}
                                  onChange={(e) => {
                                    const formattedValue = formatDollarInputValue(e.target.value);
                                    onChange(formattedValue);
                                  }}
                                  errorText={error?.message}
                                  label="Total Entity Value"
                                  tooltipWidth={400}
                                  tooltip={editInvestorAllocationTargetsTotalEntityValueTooltips[entityType]}
                                  isRequiredField
                                  withDollarSign
                                  size="md"
                                  styleType={entityType === InvestorDetailsEntityTypeEnum.Group ? 'disabled' : 'form'}
                                />
                              )}
                            />
                            <AnimatePresence>
                              {watch('capacityCalculationOption') ===
                                EditInvestorAllocationTargetsCapacityCalculationOptionEnum.OverCommitmentRatio && (
                                <motion.div key="overCommitRatio" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                                  <Label
                                    size="md"
                                    tooltip="Optionally set an over commitment ratio to over allocate slightly."
                                    tooltipWidth={400}
                                    required
                                  >
                                    OC Ratio
                                  </Label>
                                  <OverCommitRatioInputWrapper>
                                    <OverCommitRatioValue>1.</OverCommitRatioValue>
                                    <Controller
                                      name="overCommitRatio"
                                      control={control}
                                      rules={{
                                        required: {
                                          value: true,
                                          message: 'OC Ratio is required'
                                        }
                                      }}
                                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                                        <Input
                                          value={value || ''}
                                          onChange={(e) => {
                                            let inputValue = e.target.value;
                                            inputValue = inputValue.replace(/[^0-9]/g, '');
                                            if (inputValue === '00') {
                                              inputValue = '0';
                                            }
                                            if (inputValue.length > 2) {
                                              inputValue = inputValue.slice(0, 2);
                                            }
                                            onChange(inputValue);
                                          }}
                                          errorText={error?.message}
                                          width="47px"
                                          size="md"
                                          styleType="form"
                                        />
                                      )}
                                    />
                                  </OverCommitRatioInputWrapper>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </TwoColumnWrapper>
                          <RadioButtonWithNotification
                            control={control}
                            name="assetClassTargetsOption"
                            label="Asset Class Targets"
                            options={editInvestorAllocationTargetsAssetClassTargetsOptions}
                            bannerDescription={editInvestorAllocationTargetsAssetClassTargetsBannerDescription}
                          />
                          <AnimatePresence>
                            {watch('assetClassTargetsOption') ===
                              EditInvestorAllocationTargetsAssetClassTargetsOptionEnum.CustomizeTargetsForThisInvestorEntity && (
                              <motion.div key="assetClassTargets" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                                <EditAssetClassTargets control={control} assetClasses={assetClasses} />
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </EditInvestorFormFieldsWrapper>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </>
              )}
              <EditInvestorFormDevider />
            </EditInvestorFormFieldsWrapper>
            <EditInvestorFormFooter>
              <Button styleType="outline" onClick={handleClose} type="button" size="lg">
                Cancel
              </Button>
              <Button styleType={isSubmitting ? 'disabled' : 'default'} type="submit" size="lg">
                Save
              </Button>
            </EditInvestorFormFooter>
          </EditInvestorModalPageForm>
        </EditInvestorModalPageWrapper>
      </MainWrap>
    </>
  );
};

const OverCommitRatioInputWrapper = styled.div`
  display: flex;
`;

const OverCommitRatioValue = styled.div`
  font-size: 19px;
  padding: 10px 12px;
  color: ${themeVar('font.strong')};
`;
